// src/modules/Admin/pages/Investors/index.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { supabase } from '../../../../config/supabase';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  FaUserTie,
  FaPlus,
  FaEdit,
  FaTrash,
  FaTimes,
  FaCheck,
  FaSearch,
  FaSpinner,
  FaExclamationTriangle,
  FaBuilding,
  FaEnvelope,
  FaToggleOn,
  FaToggleOff,
  FaExternalLinkAlt
} from 'react-icons/fa';

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #111;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #8b5cf6;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #111;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: #333;
  }
`;

const InfoBox = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 24px;
  display: flex;
  align-items: flex-start;
  gap: 12px;

  svg {
    color: #0284c7;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .content {
    .title {
      font-size: 14px;
      font-weight: 600;
      color: #0c4a6e;
      margin-bottom: 4px;
    }
    .message {
      font-size: 13px;
      color: #075985;
    }
  }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  min-width: 150px;
  flex: 1;

  .value {
    font-size: 28px;
    font-weight: 700;
    color: #111;
    margin-bottom: 4px;
  }

  .label {
    font-size: 13px;
    color: #666;
  }
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 400px;

  input {
    width: 100%;
    padding: 12px 16px 12px 44px;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    font-size: 14px;
    transition: all 200ms;

    &:focus {
      outline: none;
      border-color: #333;
      box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
    }
  }

  svg {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
    font-size: 16px;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 16px 20px;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
`;

const Td = styled.td`
  padding: 16px 20px;
  border-bottom: 1px solid #f5f5f5;
  vertical-align: middle;
`;

const InvestorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const InvestorAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
`;

const InvestorDetails = styled.div`
  .name {
    font-size: 14px;
    font-weight: 600;
    color: #111;
    margin-bottom: 2px;
  }

  .email {
    font-size: 12px;
    color: #666;
  }
`;

const CompanyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #f3f4f6;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;

  svg {
    font-size: 11px;
    color: #6b7280;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 12px;
  background: ${props => props.$active ? '#ecfdf5' : '#fef2f2'};
  color: ${props => props.$active ? '#059669' : '#dc2626'};
`;

const DateText = styled.span`
  font-size: 13px;
  color: #666;
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #666;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: ${props => props.$danger ? '#fef2f2' : '#f5f5f5'};
    color: ${props => props.$danger ? '#dc2626' : '#111'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;

  svg {
    font-size: 48px;
    color: #ddd;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
    color: #666;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #666;

  svg {
    animation: spin 1s linear infinite;
    margin-right: 10px;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111;
`;

const ModalClose = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #f5f5f5;
  color: #666;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: #e5e5e5;
    color: #111;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  transition: all 200ms;

  &:focus {
    outline: none;
    border-color: #111;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }

  &:disabled {
    background: #f5f5f5;
    color: #999;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #333;

  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 200ms;

  ${props => props.$primary ? `
    background: #111;
    color: white;
    border: none;
    &:hover:not(:disabled) { background: #333; }
  ` : `
    background: white;
    color: #666;
    border: 1px solid #e5e5e5;
    &:hover { background: #f5f5f5; }
  `}

  ${props => props.$danger && `
    background: #ef4444;
    color: white;
    border: none;
    &:hover:not(:disabled) { background: #dc2626; }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const WarningBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #fffbeb;
  border-radius: 8px;
  border: 1px solid #fde68a;
  margin-bottom: 20px;

  svg {
    color: #f59e0b;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .content {
    .title {
      font-size: 14px;
      font-weight: 600;
      color: #92400e;
      margin-bottom: 4px;
    }

    .message {
      font-size: 13px;
      color: #a16207;
    }
  }
`;

const LinkPreview = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 12px;
  font-size: 13px;
  color: #4b5563;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: #8b5cf6;
  }

  code {
    background: #e5e7eb;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: monospace;
  }
`;

const Investors = () => {
  const { user, isAdmin, logActivity } = useAuth();
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingInvestor, setEditingInvestor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    is_active: true
  });

  useEffect(() => {
    fetchInvestors();
  }, []);

  const fetchInvestors = async () => {
    try {
      const { data, error } = await supabase
        .from('investors')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInvestors(data || []);
    } catch (error) {
      console.error('Erro ao buscar investidores:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredInvestors = investors.filter(inv => {
    const searchLower = searchTerm.toLowerCase();
    return (
      inv.name?.toLowerCase().includes(searchLower) ||
      inv.email?.toLowerCase().includes(searchLower) ||
      inv.company?.toLowerCase().includes(searchLower)
    );
  });

  const stats = {
    total: investors.length,
    active: investors.filter(i => i.is_active).length,
    inactive: investors.filter(i => !i.is_active).length
  };

  const openCreateModal = () => {
    setEditingInvestor(null);
    setFormData({
      name: '',
      email: '',
      company: '',
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = (investor) => {
    setEditingInvestor(investor);
    setFormData({
      name: investor.name || '',
      email: investor.email,
      company: investor.company || '',
      is_active: investor.is_active !== false
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingInvestor(null);
    setFormData({
      name: '',
      email: '',
      company: '',
      is_active: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email.trim()) return;

    setSaving(true);
    try {
      if (editingInvestor) {
        const { error } = await supabase
          .from('investors')
          .update({
            name: formData.name.trim(),
            company: formData.company.trim(),
            is_active: formData.is_active,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingInvestor.id);

        if (error) throw error;

        await logActivity('update_investor', 'investor', editingInvestor.id, formData.name || formData.email);

        setInvestors(investors.map(inv =>
          inv.id === editingInvestor.id
            ? { ...inv, name: formData.name.trim(), company: formData.company.trim(), is_active: formData.is_active }
            : inv
        ));
      } else {
        const { data, error } = await supabase
          .from('investors')
          .insert({
            name: formData.name.trim(),
            email: formData.email.toLowerCase().trim(),
            company: formData.company.trim(),
            is_active: formData.is_active,
            created_by: user?.email
          })
          .select()
          .single();

        if (error) throw error;

        await logActivity('create_investor', 'investor', data.id, formData.name || formData.email);

        setInvestors([data, ...investors]);
      }

      closeModal();
    } catch (error) {
      console.error('Erro ao salvar investidor:', error);
      alert('Erro ao salvar investidor. Verifique se o email já está cadastrado.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (investor) => {
    try {
      const newStatus = !investor.is_active;
      const { error } = await supabase
        .from('investors')
        .update({ is_active: newStatus, updated_at: new Date().toISOString() })
        .eq('id', investor.id);

      if (error) throw error;

      await logActivity(
        newStatus ? 'activate_investor' : 'deactivate_investor',
        'investor',
        investor.id,
        investor.name || investor.email
      );

      setInvestors(investors.map(inv =>
        inv.id === investor.id ? { ...inv, is_active: newStatus } : inv
      ));
    } catch (error) {
      console.error('Erro ao alterar status:', error);
    }
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('investors')
        .delete()
        .eq('id', showDeleteConfirm.id);

      if (error) throw error;

      await logActivity('delete_investor', 'investor', showDeleteConfirm.id, showDeleteConfirm.name || showDeleteConfirm.email);

      setInvestors(investors.filter(inv => inv.id !== showDeleteConfirm.id));
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Erro ao excluir investidor:', error);
      alert('Erro ao excluir investidor.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (name, email) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  if (!isAdmin) {
    return (
      <Container>
        <EmptyState>
          <FaUserTie />
          <h3>Acesso Restrito</h3>
          <p>Apenas administradores podem acessar esta página.</p>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <FaUserTie />
          Investidores
        </Title>
        <AddButton onClick={openCreateModal}>
          <FaPlus />
          Novo Investidor
        </AddButton>
      </Header>

      <InfoBox>
        <FaExternalLinkAlt />
        <div className="content">
          <div className="title">Dashboard de Investidores</div>
          <div className="message">
            Os investidores cadastrados terão acesso exclusivo à dashboard em{' '}
            <strong>/investidores</strong> usando login com Google.
          </div>
        </div>
      </InfoBox>

      <StatsRow>
        <StatCard>
          <div className="value">{stats.total}</div>
          <div className="label">Total de Investidores</div>
        </StatCard>
        <StatCard>
          <div className="value">{stats.active}</div>
          <div className="label">Ativos</div>
        </StatCard>
        <StatCard>
          <div className="value">{stats.inactive}</div>
          <div className="label">Inativos</div>
        </StatCard>
      </StatsRow>

      <FiltersRow>
        <SearchInput>
          <FaSearch />
          <input
            type="text"
            placeholder="Buscar por nome, email ou empresa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInput>
      </FiltersRow>

      <Card>
        {loading ? (
          <LoadingState>
            <FaSpinner />
            Carregando investidores...
          </LoadingState>
        ) : filteredInvestors.length === 0 ? (
          <EmptyState>
            <FaUserTie />
            <h3>Nenhum investidor cadastrado</h3>
            <p>Adicione investidores para que tenham acesso à dashboard exclusiva.</p>
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Investidor</Th>
                <Th>Empresa</Th>
                <Th>Status</Th>
                <Th>Último Acesso</Th>
                <Th style={{ width: '120px' }}>Ações</Th>
              </tr>
            </thead>
            <tbody>
              {filteredInvestors.map((investor) => (
                <tr key={investor.id}>
                  <Td>
                    <InvestorInfo>
                      <InvestorAvatar>
                        {getInitials(investor.name, investor.email)}
                      </InvestorAvatar>
                      <InvestorDetails>
                        <div className="name">{investor.name || 'Sem nome'}</div>
                        <div className="email">{investor.email}</div>
                      </InvestorDetails>
                    </InvestorInfo>
                  </Td>
                  <Td>
                    {investor.company ? (
                      <CompanyBadge>
                        <FaBuilding />
                        {investor.company}
                      </CompanyBadge>
                    ) : (
                      <span style={{ color: '#999' }}>-</span>
                    )}
                  </Td>
                  <Td>
                    <StatusBadge $active={investor.is_active}>
                      {investor.is_active ? 'Ativo' : 'Inativo'}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <DateText>{formatDate(investor.last_login)}</DateText>
                  </Td>
                  <Td>
                    <ActionsCell>
                      <ActionButton
                        onClick={() => handleToggleStatus(investor)}
                        title={investor.is_active ? 'Desativar' : 'Ativar'}
                      >
                        {investor.is_active ? <FaToggleOn style={{ color: '#10b981' }} /> : <FaToggleOff />}
                      </ActionButton>
                      <ActionButton onClick={() => openEditModal(investor)} title="Editar">
                        <FaEdit />
                      </ActionButton>
                      <ActionButton
                        $danger
                        onClick={() => setShowDeleteConfirm(investor)}
                        title="Excluir"
                      >
                        <FaTrash />
                      </ActionButton>
                    </ActionsCell>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {/* Modal de Criar/Editar */}
      {showModal && (
        <ModalOverlay onClick={closeModal}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingInvestor ? 'Editar Investidor' : 'Novo Investidor'}
              </ModalTitle>
              <ModalClose onClick={closeModal}>
                <FaTimes />
              </ModalClose>
            </ModalHeader>
            <ModalBody>
              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label>Nome</Label>
                  <Input
                    type="text"
                    placeholder="Nome completo"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    placeholder="email@empresa.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    disabled={editingInvestor}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Empresa</Label>
                  <Input
                    type="text"
                    placeholder="Nome da empresa"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    Acesso ativo
                  </CheckboxLabel>
                </FormGroup>

                {!editingInvestor && (
                  <>
                    <WarningBox>
                      <FaExclamationTriangle />
                      <div className="content">
                        <div className="title">Importante</div>
                        <div className="message">
                          O investidor precisará fazer login com uma conta Google que tenha este email.
                        </div>
                      </div>
                    </WarningBox>

                    <LinkPreview>
                      <FaExternalLinkAlt />
                      <span>Acesso em: <code>/investidores</code></span>
                    </LinkPreview>
                  </>
                )}

                <ModalActions>
                  <Button type="button" onClick={closeModal}>
                    Cancelar
                  </Button>
                  <Button type="submit" $primary disabled={saving || !formData.email.trim()}>
                    {saving ? <FaSpinner /> : <FaCheck />}
                    {editingInvestor ? 'Salvar' : 'Cadastrar'}
                  </Button>
                </ModalActions>
              </form>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteConfirm && (
        <ModalOverlay onClick={() => setShowDeleteConfirm(null)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Confirmar Exclusão</ModalTitle>
              <ModalClose onClick={() => setShowDeleteConfirm(null)}>
                <FaTimes />
              </ModalClose>
            </ModalHeader>
            <ModalBody>
              <WarningBox>
                <FaExclamationTriangle />
                <div className="content">
                  <div className="title">Atenção!</div>
                  <div className="message">
                    Você está prestes a excluir o investidor <strong>{showDeleteConfirm.name || showDeleteConfirm.email}</strong>.
                    Esta ação não pode ser desfeita.
                  </div>
                </div>
              </WarningBox>

              <ModalActions>
                <Button type="button" onClick={() => setShowDeleteConfirm(null)}>
                  Cancelar
                </Button>
                <Button type="button" $danger onClick={handleDelete} disabled={saving}>
                  {saving ? <FaSpinner /> : <FaTrash />}
                  Excluir
                </Button>
              </ModalActions>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default Investors;
